import { Button, ButtonProps } from '@websrc/App/Components/Button/Button'
import { trackAddToCart } from '@websrc/App/Helpers/tracking'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useSettings } from '@websrc/App/Hooks/useSettings'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { useBasketApi } from '@websrc/Basket/useBasketApi'
import { useBasketStateOptional } from '@websrc/Basket/useBasketState'
import { useProductMeta } from '@websrc/Basket/useProductMeta'
import { mapProductPage } from '@websrc/DataMappers/mapProductPage'
import clsx from 'clsx'
import { styles, typography } from 'leds'
import React, { useState } from 'react'

import { BUTTON_WIDTH } from '../../../Button/Button'
import { WhereToBuyButton } from './WhereToBuyButton/WhereToBuyButton'

export type ProductInfoButtonsProps = {
  addToCartButtonRef?: React.MutableRefObject<any>
  productDetails: Partial<ReturnType<typeof mapProductPage>['product_details']>
  quantity: number
} & ButtonProps

export const ProductInfoButtons = ({
  productDetails,
  quantity,
  width = BUTTON_WIDTH.Auto,
  size = 'medium',
  addToCartButtonRef,
  type = 'primary',
}: ProductInfoButtonsProps) => {
  const [hasError, setHasError] = useState(false)
  const basketApi = useBasketApi()
  const s = useSettings()
  const t = useStaticText()
  const locale = useLocale()
  const basket = useBasketStateOptional()

  const {
    product_number,
    product_status,
    price_discount,
    channelsight_asset_id,
    channelsight_virtual_sku,
  } = productDetails

  const productMeta = useProductMeta(product_number ?? '')

  if (productDetails.is_retired) {
    return (
      <Button
        aria-label={t.ecom.retired_contact_us}
        color={'black'}
        colorMode={'light'}
        href={
          locale == 'en-us' ? `/${locale}${s.requestMeetingUrl}` : `/${locale}${s.distributorUrl}`
        }
        reference={addToCartButtonRef}
        size={size}
        text={t.ecom.retired_contact_us}
        type={type}
        width={width}
      />
    )
  }

  if (locale !== 'en-us') {
    return (
      <WhereToBuyButton
        assetId={channelsight_asset_id}
        color={'black'}
        colorMode={'light'}
        productNumber={channelsight_virtual_sku ? channelsight_virtual_sku : product_number}
        reference={addToCartButtonRef}
        size={'medium'}
        type={'primary'}
        width={width}
      />
    )
  }

  const textLabel = product_status === 'E2' ? t.ecom.preorder : t.ecom.add_to_cart
  return (
    <>
      <Button
        color={'black'}
        colorMode={'light'}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()

          if (hasError) {
            setHasError(false)
          }

          if (basket) {
            trackAddToCart(
              'product add to cart',
              productMeta,
              quantity,
              price_discount ?? '0',
              basket
            )
          }

          void basketApi(`/items/add/${product_number}/${quantity}`, 'POST').then(
            ([err]) => err && setHasError(true)
          )
        }}
        reference={addToCartButtonRef}
        size={size}
        text={textLabel}
        type={type}
        width={width}
      />

      {hasError && (
        <p className={clsx(styles({ marginY: 25 }), typography({ size: 'sm', color: 'error' }))}>
          {t.ecom.error_message_add_to_cart}
        </p>
      )}
    </>
  )
}
